import { useState, useCallback } from 'react';
import { categoryService } from '@/services';
import type { Category, CreateCategoryRequest, UpdateCategoryRequest, QueryParams } from '@/types/assessment';

export const useCategories = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchCategories = useCallback(async (params?: QueryParams) => {
    try {
      setLoading(true);
      setError(null);
      const response = await categoryService.getCategories(params);
      
      if (response.success && response.data) {
        setCategories(response.data.categories || []);
      } else {
        throw new Error(response.message || 'Failed to fetch categories');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch categories';
      setError(errorMessage);
      console.error('Error fetching categories:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchActiveCategories = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await categoryService.getActiveCategories();
      
      if (response.success && response.data) {
        setCategories(response.data.categories || []);
      } else {
        throw new Error(response.message || 'Failed to fetch active categories');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch active categories';
      setError(errorMessage);
      console.error('Error fetching active categories:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const createCategory = useCallback(async (categoryData: CreateCategoryRequest) => {
    try {
      setLoading(true);
      setError(null);
      const response = await categoryService.createCategory(categoryData);
      
      if (response.success && response.data) {
        return response.data.category;
      } else {
        throw new Error(response.message || 'Failed to create category');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create category';
      setError(errorMessage);
      console.error('Error creating category:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const updateCategory = useCallback(async (id: string, categoryData: UpdateCategoryRequest) => {
    try {
      setLoading(true);
      setError(null);
      const response = await categoryService.updateCategory(id, categoryData);
      
      if (response.success && response.data) {
        return response.data.category;
      } else {
        throw new Error(response.message || 'Failed to update category');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update category';
      setError(errorMessage);
      console.error('Error updating category:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteCategory = useCallback(async (id: string) => {
    try {
      setLoading(true);
      setError(null);
      const response = await categoryService.deleteCategory(id);
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to delete category');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete category';
      setError(errorMessage);
      console.error('Error deleting category:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const toggleCategoryStatus = useCallback(async (id: string) => {
    try {
      setLoading(true);
      setError(null);
      const response = await categoryService.toggleCategoryStatus(id);
      
      if (response.success && response.data) {
        return response.data.category;
      } else {
        throw new Error(response.message || 'Failed to toggle category status');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to toggle category status';
      setError(errorMessage);
      console.error('Error toggling category status:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const getCategory = useCallback(async (id: string) => {
    try {
      setLoading(true);
      setError(null);
      const response = await categoryService.getCategory(id);
      
      if (response.success && response.data) {
        return response.data.category;
      } else {
        throw new Error(response.message || 'Failed to fetch category');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch category';
      setError(errorMessage);
      console.error('Error fetching category:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const exportCategories = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const blob = await categoryService.exportCategories();
      
      // Download the file
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `categories-export-${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to export categories';
      setError(errorMessage);
      console.error('Error exporting categories:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const importCategories = useCallback(async (file: File) => {
    try {
      setLoading(true);
      setError(null);
      const response = await categoryService.importCategories(file);
      
      if (response.success && response.data) {
        return response.data;
      } else {
        throw new Error(response.message || 'Failed to import categories');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to import categories';
      setError(errorMessage);
      console.error('Error importing categories:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    categories,
    loading,
    error,
    fetchCategories,
    fetchActiveCategories,
    createCategory,
    updateCategory,
    deleteCategory,
    toggleCategoryStatus,
    getCategory,
    exportCategories,
    importCategories,
  };
};